import React from "react";
import { useNavigate } from "react-router-dom";
import PublicNav from "./components/PublicNav";
import PublicFooter from "./components/PublicFooter";

function SectionLabel({ children }) {
  return (
    <span className="block font-mono text-[1.7rem] tracking-[0.18em] uppercase text-brand-gold/60 mb-6">
      {children}
    </span>
  );
}

function GoldBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="bg-brand-gold text-[#0d0d0c] font-bold px-10 py-[1.1rem] text-[0.9rem] tracking-[0.08em] uppercase hover:bg-brand-gold/85 transition-colors border-0 cursor-pointer"
    >
      {children}
    </button>
  );
}

function GhostBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="bg-transparent text-text-muted border border-white/20 px-10 py-[1.1rem] text-[0.8rem] tracking-[0.08em] uppercase hover:border-white/40 hover:text-text-primary transition-colors cursor-pointer"
    >
      {children}
    </button>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-base">
      <PublicNav bandId={null} onHome={() => navigate("/")} />

      {/* ── HERO ── */}
      <section className="flex flex-col items-center justify-center min-h-screen px-6 pt-28 pb-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 20%, rgba(201,168,76,0.07) 0%, transparent 70%)",
          }}
        />
        <SectionLabel>Built for working musicians</SectionLabel>
        <h1 className="font-display text-[clamp(2.6rem,7vw,5.5rem)] font-normal leading-[1.05] max-w-4xl mb-6 text-text-primary">
          The setlist you rehearsed
          <br />
          is the <em className="italic text-brand-gold">one you play.</em>
        </h1>
        <p className="text-text-secondary text-[clamp(1rem,2vw,1.2rem)] font-light max-w-lg mb-12 leading-relaxed">
          Lock your setlist before you leave rehearsal. Cache the charts on
          every device. Walk into the venue without signal. No surprises when
          the drummer counts in.
        </p>
        <div className="flex flex-col items-center gap-4">
          <GoldBtn onClick={() => navigate("/signup")}>
            Build Your Setlist — Free
          </GoldBtn>
          <span className="text-[0.75rem] text-text-muted tracking-[0.04em]">
            No credit card required. Up to 20 songs free.
          </span>
        </div>
      </section>

      {/* ── SCENE ── */}
      <section className="bg-surface border-t border-b border-white/[0.06] px-6 py-20 text-center">
        <div className="max-w-xl mx-auto">
          <p className="font-display text-[clamp(1.25rem,3vw,1.75rem)] text-text-primary">
            The drummer counts in.
          </p>
          <p className="font-display text-[clamp(1.25rem,3vw,1.75rem)] text-text-primary mt-5">
            You swipe to the chart.
          </p>
          <p className="font-display text-[clamp(1.25rem,3vw,1.75rem)] text-brand-gold italic mt-5">
            It doesn't load.
          </p>
          <p className="text-text-muted text-[1rem] font-light mt-10 max-w-sm mx-auto leading-relaxed">
            That's not bad luck. That's what happens when your tools weren't
            built for the stage.
          </p>
        </div>
      </section>

      {/* ── STORY ── */}
      <section className="max-w-[680px] mx-auto px-6 py-24">
        <SectionLabel>How this started</SectionLabel>
        <h2 className="font-display text-[clamp(1.75rem,4vw,2.75rem)] font-normal leading-[1.2] mb-8 text-text-primary">
          A chicken coop on a dirt road taught me everything wrong with every
          other tool.
        </h2>
        <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed mb-5">
          Our practice space was an old converted chicken coop out on a dirt
          road. Cell service was rough. Sometimes you had a bar. Most of the
          time you had nothing.
        </p>
        <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed mb-5">
          Every rehearsal started the same way. Everyone pulling up charts from
          Google Drive, waiting, refreshing, waiting again. Half the band would
          get them. Half wouldn't. We'd cobble together a setlist by stitching
          different Google Docs into one document using whatever free service we
          could find that week.
        </p>
        <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed mb-5">
          <strong className="text-text-primary font-medium">
            It was always a pain in the butt.
          </strong>{" "}
          And it was always our problem to solve, every single time, on every
          single device, before we could play a single note.
        </p>
        <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed mb-10">
          I kept thinking: someone should fix this. There has to be a better way
          to do something this simple. Get every musician in the room looking at
          the same chart, in the same order, without needing a WiFi signal to
          make it happen.
        </p>
        <div className="border-l-2 border-brand-gold pl-7 my-10">
          <p className="font-display text-[clamp(1.125rem,2.5vw,1.5rem)] italic text-text-primary">
            "Nobody builds tools for where bands actually practice. They build
            them for where they imagine bands practice."
          </p>
        </div>
        <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed">
          So I built BandOS. Not for the ideal rehearsal space. For the real
          one. The chicken coop, the church basement, the garage with one bar of
          signal. And especially for the venue on the night of the gig, when
          none of that matters anymore because it's already done.
        </p>
      </section>

      {/* ── REFRAME ── */}
      <section className="bg-surface border-t border-b border-white/[0.06] px-6 py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <SectionLabel>The real problem</SectionLabel>
          <h2 className="font-display text-[clamp(1.875rem,5vw,3.5rem)] font-normal leading-[1.1] mb-8 text-text-primary">
            Your band isn't disorganized.
            <br />
            <em className="italic text-brand-gold">
              Your tools assume the wrong things.
            </em>
          </h2>
          <p className="text-text-secondary text-[1.0625rem] font-light max-w-2xl mx-auto mb-5 leading-relaxed">
            Every setlist app on the market was built on two assumptions: that
            you'll have WiFi when it matters, and that nobody will change
            anything last minute.
          </p>
          <p className="text-text-secondary text-[1.0625rem] font-light max-w-2xl mx-auto mb-5 leading-relaxed">
            <strong className="text-text-primary font-medium">
              Both assumptions are wrong. Every single time.
            </strong>
          </p>
          <p className="text-text-secondary text-[1.0625rem] font-light max-w-2xl mx-auto mb-5 leading-relaxed">
            Venue WiFi goes down. A band member edits the setlist from the
            parking lot. Someone opens an old version on their tablet. The keys
            player doesn't know you dropped song three.
          </p>
          <p className="text-text-secondary text-[1.0625rem] font-light max-w-2xl mx-auto leading-relaxed">
            These aren't flukes. They're what happens when your tools were built
            for convenience and not for the stage.
          </p>
        </div>
      </section>

      {/* ── FAILURES ── */}
      <section className="max-w-3xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <SectionLabel>What keeps happening</SectionLabel>
          <h2 className="font-display text-[clamp(1.75rem,4vw,3rem)] font-normal mb-4 text-text-primary">
            Three failures. Every gigging band has felt all of them.
          </h2>
          <p className="text-text-muted text-[1rem]">
            If any of these sound familiar, it's not bad luck. It's a structural
            problem with every other tool.
          </p>
        </div>
        <div className="flex flex-col gap-px bg-border">
          {[
            {
              num: "01",
              title: "The chart that wouldn't load.",
              body: "You're at the venue. The WiFi is either dead, shared with 200 guests, or requires a login nobody has. Half the band can pull up their charts. Half can't. You're standing at soundcheck troubleshooting a file sync problem instead of playing.",
              moment:
                '"We had to wing the last three songs. The guitarist didn\'t have the chart and we couldn\'t get signal. The client noticed."',
            },
            {
              num: "02",
              title: "The setlist nobody agreed on.",
              body: "You rehearsed a setlist three times. It was tight. Then someone changed the order on the way to the gig. Or the night before. Or an hour before doors. Nobody told the keys player. The drummer counted in the wrong song.",
              moment:
                '"Someone changed the order on the way to the gig and nobody told the keys player. The first two minutes were a disaster."',
            },
            {
              num: "03",
              title: "The wrong version of everything.",
              body: "You updated the chart after rehearsal. Fixed the bridge, changed the key. But three out of five band members still have the old version on their devices. Nobody knows whose version is right until you're already on stage and something sounds off.",
              moment:
                '"I\'ve lost count of how many times someone shows up with a completely different version of the chart than what we rehearsed. It\'s maddening."',
            },
          ].map(({ num, title, body, moment }) => (
            <div
              key={num}
              className="bg-base border border-white/[0.06] p-10"
            >
              <span className="block font-mono text-[1rem] tracking-[0.15em] text-brand-gold/60 mb-4">
                {num}
              </span>
              <h3 className="font-display text-[1.375rem] font-normal mb-4 text-text-primary">
                {title}
              </h3>
              <p className="text-text-secondary text-[1rem] font-light leading-relaxed">
                {body}
              </p>
              <div className="mt-5 pl-5 border-l-2 border-danger/40">
                <p className="font-display italic text-[0.9375rem] text-text-muted">
                  {moment}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MECHANISM ── */}
      <section className="bg-surface border-t border-b border-white/[0.06] px-6 py-24">
        <div className="max-w-3xl mx-auto">
          <SectionLabel>The solution</SectionLabel>
          <h2 className="font-display text-[clamp(1.75rem,4vw,3rem)] font-normal leading-[1.2] mb-4 text-text-primary">
            Introducing{" "}
            <em className="italic text-brand-gold">
              Pre-Show Lock &amp; Cache.
            </em>
          </h2>
          <p className="text-text-secondary text-[1.0625rem] font-light mb-14 max-w-2xl leading-relaxed">
            BandOS is built around one idea that every other tool missed:{" "}
            <strong className="text-text-primary font-medium">
              preparation is the performance.
            </strong>{" "}
            What you lock before you leave rehearsal is what shows up on stage.
            On every device that's prepared. No WiFi needed.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border mb-14">
            {[
              {
                title: "Offline-First Caching",
                body: "Every chart in your prepared setlists gets cached directly on each device. Not in the cloud. Not dependent on a signal. Prepare before you leave rehearsal. Walk into a venue with zero bars. Open BandOS. Everything you're playing that night is already there.",
              },
              {
                title: "Setlist Version Lock",
                body: "You lock the setlist before you leave rehearsal. Once it's locked, nobody changes the order, nobody drops in a last-minute song, nobody touches it without going through an explicit unlock. What you rehearsed is what you play. The lock is the point.",
              },
              {
                title: "Stale Chart Detection",
                body: "If a chart has been updated since a band member last cached their device, BandOS flags it before the show. Not during it. Everyone knows before the drummer counts in, not after.",
              },
              {
                title: "Per-Device Preparation",
                body: "Every band member prepares their own device. Not just the admin. Not just the leader. Each device caches independently so there's no single point of failure and no single person responsible for everyone else's setup.",
              },
            ].map(({ title, body }) => (
              <div
                key={title}
                className="bg-base border border-white/[0.06] p-8"
              >
                <div className="w-9 h-9 bg-brand-gold/10 border border-brand-gold/40 mb-5 flex items-center justify-center text-brand-gold text-lg">✓</div>
                <h4 className="text-[0.9375rem] font-medium mb-3 text-text-primary">
                  {title}
                </h4>
                <p className="text-text-muted text-[0.875rem] font-light leading-[1.65]">
                  {body}
                </p>
              </div>
            ))}
          </div>
          <div className="border border-white/[0.06] bg-brand-gold/[0.03] p-10 text-center">
            <p className="font-display italic text-[clamp(1.125rem,2.5vw,1.5rem)] text-text-primary mb-3">
              "Prepare your setlists before you leave rehearsal. Walk into a
              venue with no WiFi. Open BandOS cold, hit Go Live. Every chart
              your band needs is already there."
            </p>
            <span className="font-mono text-[0.8rem] text-text-muted tracking-[0.1em]">
              What they rehearsed is what they play. No caveats.
            </span>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <SectionLabel>How it works</SectionLabel>
        <h2 className="font-display text-[clamp(1.75rem,4vw,3rem)] font-normal mb-4 text-text-primary">
          Three steps. That's it.
        </h2>
        <p className="text-text-muted text-[1rem] mb-16">
          The workflow is simple by design. Because the night of the gig, you
          shouldn't be thinking about the tools.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border">
          {[
            {
              num: "01",
              title: "Upload your charts.",
              body: "PDFs, chord charts, lead sheets. Upload everything into BandOS and organize by song. Your whole library in one place.",
            },
            {
              num: "02",
              title: "Build and lock your setlist.",
              body: "Drag songs into order. Share with the band. When you're ready, lock it. Nobody changes it. Everyone sees the same version.",
            },
            {
              num: "03",
              title: "Cache and go live.",
              body: "Every band member caches their device before leaving rehearsal. At the venue, hit Go Live. Everything is already there. Now go play.",
            },
          ].map(({ num, title, body }) => (
            <div
              key={num}
              className="bg-base border border-white/[0.06] p-10 text-left"
            >
              <span className="block font-mono text-[3rem] font-medium text-white/25 leading-none mb-5">
                {num}
              </span>
              <h3 className="text-[1.0625rem] font-medium mb-3 text-text-primary">
                {title}
              </h3>
              <p className="text-text-muted text-[0.875rem] font-light leading-[1.65]">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PERSONAS ── */}
      <section className="bg-surface border-t border-b border-white/[0.06] px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Who it's built for</SectionLabel>
            <h2 className="font-display text-[clamp(1.75rem,4vw,3rem)] font-normal mb-4 text-text-primary">
              If you're the one responsible when something goes wrong, this is
              for you.
            </h2>
            <p className="text-text-muted">
              BandOS was built for the leader who carries the gig on their
              shoulders.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
            {[
              {
                tag: "Worship Leaders",
                title: "Sunday morning doesn't have a retry button.",
                body: "You've rehearsed the team all week. The service order is set. The congregation is in the room. This isn't the moment for a loading screen or a version conflict. BandOS makes sure what you prepared in rehearsal is exactly what your team sees when the first chord hits. Every screen. Regardless of the church's WiFi.",
              },
              {
                tag: "Cover Band Leaders",
                title: "Your reputation is built one gig at a time.",
                body: "Every venue you play is forming an opinion. A tight show builds a reputation. A disorganized one chips away at it. Wrong song, missing chart, a version nobody rehearsed. BandOS makes sure what happens on stage is what you built in the rehearsal room.",
              },
              {
                tag: "Wedding Band MDs",
                title: "One bad moment at a wedding follows you forever.",
                body: "The first dance song loaded on your iPad but not the guitarist's. He played the wrong intro. The bride noticed. In the wedding market, your next booking comes from how well you did the last one. BandOS makes sure the charts your band walks on stage with are the charts you rehearsed. Full stop.",
              },
            ].map(({ tag, title, body }) => (
              <div
                key={tag}
                className="bg-base border border-white/[0.06] p-10"
              >
                <span className="block font-mono text-[0.85rem] tracking-[0.18em] uppercase text-brand-gold mb-5">
                  {tag}
                </span>
                <h3 className="font-display text-[1.375rem] font-normal leading-[1.2] mb-4 text-text-primary">
                  {title}
                </h3>
                <p className="text-text-secondary text-[0.9375rem] font-light leading-[1.7]">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AFTER STATE ── */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <SectionLabel>What changes</SectionLabel>
        <h2 className="font-display text-[clamp(1.875rem,5vw,3.5rem)] font-normal leading-[1.1] mb-12 text-text-primary">
          Imagine walking into
          <br />a venue with{" "}
          <em className="italic text-brand-gold">nothing left to worry about.</em>
        </h2>
        <div className="text-left flex flex-col gap-px bg-border mb-12">
          {[
            <>
              <strong className="text-text-primary font-medium">
                The charts are already on every device.
              </strong>{" "}
              Cached the night before. No loading. No waiting. No checking the
              signal.
            </>,
            <>
              <strong className="text-text-primary font-medium">
                The setlist is locked.
              </strong>{" "}
              Nobody changed it. Nobody added a song. Nobody reordered anything
              without you knowing. What you rehearsed is what you play.
            </>,
            <>
              <strong className="text-text-primary font-medium">
                Every band member is on the same version.
              </strong>{" "}
              Same charts. Same order. The stale chart warning caught the one
              update before the show, not during it.
            </>,
            <>
              <strong className="text-text-primary font-medium">
                You're thinking about the music.
              </strong>{" "}
              Not the tools. Not the WiFi. Not whether someone changed something.
              Just the performance.
            </>,
          ].map((text, i) => (
            <div
              key={i}
              className="bg-base border border-white/[0.06] p-7 flex items-start gap-5"
            >
              <div className="w-5 h-5 bg-brand-gold flex-shrink-0 mt-0.5 flex items-center justify-center text-[#0d0d0c] text-[0.65rem] font-bold">
                ✓
              </div>
              <p className="text-text-secondary text-[1.0625rem] font-light leading-relaxed">
                {text}
              </p>
            </div>
          ))}
        </div>
        <p className="font-display italic text-[clamp(1.125rem,2.5vw,1.5rem)] text-text-muted">
          "If I'm thinking about whether the tech is going to work, I'm not
          thinking about the music."
        </p>
      </section>

      {/* ── PRICING ── */}
      <section className="bg-surface border-t border-b border-white/[0.06] px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="font-display text-[clamp(1.75rem,4vw,3rem)] font-normal mb-4 text-text-primary">
              Free to start. Serious when it matters.
            </h2>
          </div>
          <p className="text-text-muted text-[1rem] font-light text-center max-w-2xl mx-auto mb-14 leading-relaxed">
            One lost booking in the wedding market costs thousands. One
            disrupted service costs a worship leader far more than that. A full
            year of BandOS costs less than a single bad gig.{" "}
            <strong className="text-text-primary font-medium">
              Start free. Upgrade when the stakes are high enough to matter.
            </strong>
          </p>
          <div className="flex flex-wrap justify-center gap-px bg-border mb-8">
            {/* Free */}
            <div className="bg-base border border-white/[0.08] p-10 flex flex-col flex-[1_1_240px] max-w-[300px]">
              <span className="block font-mono text-[0.68rem] tracking-[0.2em] uppercase text-brand-gold mb-5">
                Free
              </span>
              <div className="font-display text-[3rem] font-normal leading-none text-text-primary mb-1">
                $0
              </div>
              <div className="text-[0.8rem] text-text-muted font-light mb-7">
                Forever free
              </div>
              <p className="text-[0.875rem] text-text-secondary font-light leading-[1.6] mb-7">
                Build your setlist. Get rehearsal-ready. See exactly what BandOS
                does before spending a dollar.
              </p>
              <ul className="list-none flex-1 flex flex-col gap-0 mb-9">
                {["Up to 20 songs", "1 saved setlist", "Live Mode", "Offline caching"].map(
                  (f) => (
                    <li
                      key={f}
                      className="flex items-center gap-3 text-[0.875rem] text-text-secondary font-light border-b border-white/[0.06] py-2"
                    >
                      <span className="text-brand-gold/70 text-[0.75rem]">
                        —
                      </span>
                      {f}
                    </li>
                  )
                )}
              </ul>
              <GhostBtn onClick={() => navigate("/signup")}>Start Free</GhostBtn>
            </div>

            {/* Solo — featured */}
            <div className="relative bg-brand-gold/[0.04] border border-brand-gold/60 p-10 flex flex-col flex-[1_1_240px] max-w-[300px]">
              <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-brand-gold text-[#0d0d0c] font-mono text-[0.6rem] tracking-[0.15em] uppercase font-bold px-4 py-1.5 whitespace-nowrap">
                Most Popular
              </div>
              <span className="block font-mono text-[0.68rem] tracking-[0.2em] uppercase text-brand-gold mb-5">
                Solo
              </span>
              <div className="font-display text-[3rem] font-normal leading-none text-text-primary mb-1">
                $9
              </div>
              <div className="text-[0.8rem] text-text-muted font-light mb-7">
                per month
              </div>
              <p className="text-[0.875rem] text-text-secondary font-light leading-[1.6] mb-7">
                Personal reliability for the serious working musician. Unlimited
                songs, unlimited setlists.
              </p>
              <ul className="list-none flex-1 flex flex-col gap-0 mb-9">
                {[
                  "Unlimited songs",
                  "Unlimited setlists",
                  "PDF export",
                  "Live Mode",
                  "Offline caching",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-3 text-[0.875rem] text-text-secondary font-light border-b border-white/[0.06] py-2"
                  >
                    <span className="text-brand-gold/70 text-[0.75rem]">—</span>
                    {f}
                  </li>
                ))}
              </ul>
              <GoldBtn onClick={() => navigate("/signup")}>Start Free</GoldBtn>
            </div>

            {/* Band */}
            <div className="bg-base border border-white/[0.08] p-10 flex flex-col flex-[1_1_240px] max-w-[300px]">
              <span className="block font-mono text-[0.68rem] tracking-[0.2em] uppercase text-brand-gold mb-5">
                Band
              </span>
              <div className="font-display text-[3rem] font-normal leading-none text-text-primary mb-1">
                $15
              </div>
              <div className="text-[0.8rem] text-text-muted font-light mb-7">
                per month
              </div>
              <p className="text-[0.875rem] text-text-secondary font-light leading-[1.6] mb-7">
                The full pre-show system for the whole group. Shared library,
                shared source of truth. When someone updates a chart, anyone
                who opens BandOS before the show will see exactly which charts
                need a refresh.
              </p>
              <ul className="list-none flex-1 flex flex-col gap-0 mb-9">
                {[
                  "Everything in Solo",
                  "Invite band members",
                  "Shared chart library",
                  "Role-based permissions",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-3 text-[0.875rem] text-text-secondary font-light border-b border-white/[0.06] py-2"
                  >
                    <span className="text-brand-gold/70 text-[0.75rem]">—</span>
                    {f}
                  </li>
                ))}
              </ul>
              <GhostBtn onClick={() => navigate("/signup")}>Start Free</GhostBtn>
            </div>
          </div>
          <p className="text-center text-[0.8rem] text-text-muted font-light">
            No credit card required to start. Upgrade anytime from inside the
            app.
          </p>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="px-6 py-32 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(201,168,76,0.05) 0%, transparent 70%)",
          }}
        />
        <div className="max-w-3xl mx-auto relative">
          <SectionLabel>Your next rehearsal is coming up</SectionLabel>
          <h2 className="font-display text-[clamp(2rem,5vw,3.75rem)] font-normal leading-[1.1] mb-6 text-text-primary">
            The kind of leader you are doesn't leave the gig{" "}
            <em className="italic text-brand-gold">to chance.</em>
          </h2>
          <p className="text-text-muted text-[1.125rem] font-light max-w-2xl mx-auto mb-12 leading-relaxed">
            Lock the setlist. Cache the charts. Walk on stage thinking about
            nothing except playing well. That's what BandOS is for.
          </p>
          <div className="flex flex-col items-center gap-4">
            <GoldBtn onClick={() => navigate("/signup")}>
              Build Your Setlist Free
            </GoldBtn>
            <span className="text-[0.75rem] text-text-muted tracking-[0.04em]">
              Free forever. No card required. Ready before your next rehearsal.
            </span>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
